import Link from 'next/link';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

type RenderMarkdownOptions = {
    /**
     * Bold runs render one step larger by default (chat answers use it as a
     * heading). Step-by-step answers bold individual control names inside a
     * sentence, where the larger size reads as noise — pass false there.
     */
    emphasizeStrong?: boolean;
};

export const renderMarkdown = (text?: string | null, { emphasizeStrong = true }: RenderMarkdownOptions = {}) => {
    if (!text) return null;

    return (
        <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
                a: ({ href, ...props }) => (
                    <Link
                        href={href}
                        {...props}
                        rel="noopener noreferrer"
                        className="tw-link tw-font-bold tw-underline tw-transition tw-duration-200"
                    />
                ),
                ul: ({ children, ...props }) => (
                    <ul
                        className="tw-mb-2 tw-mt-2 tw-flex tw-list-inside tw-list-disc tw-flex-col tw-gap-2 tw-p-0 first:tw-mt-0 last:tw-mb-0"
                        {...props}
                    >
                        {children}
                    </ul>
                ),
                ol: ({ children, ...props }) => (
                    <ol
                        className="tw-mb-2 tw-mt-2 tw-flex tw-list-inside tw-list-decimal tw-flex-col tw-gap-2 tw-p-0 first:tw-mt-0 last:tw-mb-0"
                        {...props}
                    >
                        {children}
                    </ol>
                ),
                li: ({ children, ...props }) => (
                    <li className="tw-ml-3" {...props}>
                        {children}
                    </li>
                ),
                p: ({ children, ...props }) => (
                    <p className="tw-leading-2 tw-mx-0 tw-mb-2 tw-mt-0 last:tw-mb-0" {...props}>
                        {children}
                    </p>
                ),
                strong: ({ children }) => (
                    <strong className={emphasizeStrong ? 'tw-text-[17px] tw-font-semibold' : 'tw-font-semibold'}>
                        {children}
                    </strong>
                ),
                em: ({ children }) => <em className="tw-italic">{children}</em>,
                blockquote: ({ children }) => (
                    <blockquote className="tw-border-l-2 tw-pl-3 tw-italic tw-text-gray-300">{children}</blockquote>
                )
            }}
        >
            {text}
        </ReactMarkdown>
    );
};
